import cloneDeep from "lodash/cloneDeep";
import has from "lodash/has";
import get from "lodash/get";
import omit from "lodash/omit";

const API_NAME = "rota";
const API_VERSION = "/v1";
const CONTENT_TYPE = "application/vnd.coactive.rota+json";

const actions = {
  async getRota(context, params) {
    return this.$api.get(API_NAME, `${API_VERSION}/rotas/${params.id}`);
  },

  async getCurrentRotas() {
    return this.$api
      .get(API_NAME, `${API_VERSION}/rotas/current`)
      .then((response) => {
        if (has(response, "_embedded.rotas")) {
          return response._embedded.rotas;
        }
        return [];
      });
  },
  async getRotas(context, payload) {
    const myInit = {};

    // payload contains filter/sort/pagination options.
    if (payload) {
      myInit.queryStringParameters = {
        page: payload.page,
        pageSize: payload.pageSize ? payload.pageSize : 20,
      };

      if (has(payload, "sortBy")) {
        let sortField = payload.sortBy;
        if (sortField === "dateCreated") {
          sortField = "_id";
        }
        let sortOrder = 1;
        if (has(payload, "sortDesc") && payload.sortDesc === true) {
          sortOrder = -1;
        }
        myInit.queryStringParameters.sort = JSON.stringify({
          [sortField]: sortOrder,
        });
      }

      if (has(payload, "filter")) {
        myInit.queryStringParameters.filter = JSON.stringify(payload.filter);
      }
    }

    return this.$api
      .get(API_NAME, `${API_VERSION}/rotas`, myInit)
      .then((response) => {
        if (has(response, "_embedded.rotas")) {
          return response;
        }
        return false;
      });
  },
  /**
   * Fetch non-draft rotas for the given day
   *
   * @param {*} context
   * @param {*} payload
   * @returns array
   */
  async getRotasForDay(context, payload) {
    const myInit = {};
    myInit.queryStringParameters = {
      filter: JSON.stringify([
        { subject: "startDate", comparison: "lte", value: payload.day },
        { subject: "endDate", comparison: "gte", value: payload.day },
      ]),
      publishedOnly: true,
    };
    return this.$api
      .get(API_NAME, `${API_VERSION}/rotas`, myInit)
      .then((response) => {
        if (has(response, "_embedded.rotas")) {
          return response._embedded.rotas;
        }
        return [];
      });
  },
  /**
   * Fetch non-draft rotas for the given period
   *
   * @param {*} context
   * @param {*} payload
   * @returns array
   */
  async getRotasForPeriod(context, payload) {
    const myInit = {};
    myInit.queryStringParameters = {
      filter: JSON.stringify([
        { subject: "startDate", comparison: "lte", value: payload.endDate },
        { subject: "endDate", comparison: "gte", value: payload.startDate },
      ]),
      publishedOnly: true,
    };
    return this.$api
      .get(API_NAME, `${API_VERSION}/rotas`, myInit)
      .then((response) => {
        if (has(response, "_embedded.rotas")) {
          return response._embedded.rotas;
        }
        return [];
      });
  },
  async createRota(context, payload) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: payload.rota,
    };

    return this.$api.post(API_NAME, `${API_VERSION}/rotas`, myInit);
  },

  async publishRota(context, payload) {
    const myInit = {};
    if (get(payload, "sendEmail", false) === true) {
      myInit.queryStringParameters = { "send-email": "true" };
    }
    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rotas/${payload.id}/publish`,
      myInit,
    );
  },

  async withdrawRota(context, payload) {
    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rotas/${payload.id}/withdraw`,
    );
  },

  async lockRota(context, payload) {
    return this.$api.post(API_NAME, `${API_VERSION}/rotas/${payload.id}/lock`);
  },

  async deleteRota(context, payload) {
    return this.$api.del(API_NAME, `${API_VERSION}/rotas/${payload.id}`);
  },

  async createShift(context, payload) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: payload.shift,
    };

    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rotas/${payload.rota.id}/shifts`,
      myInit,
    );
  },
  async updateShift(context, payload) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
    };
    myInit.body = cloneDeep(payload.shift);
    delete myInit.body.id;

    return this.$api.patch(
      API_NAME,
      `${API_VERSION}/rotas/${payload.rota.id}/shifts/${payload.shift.id}`,
      myInit,
    );
  },
  async deleteShift(context, payload) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
    };

    return this.$api.del(
      API_NAME,
      `${API_VERSION}/rotas/${payload.rota.id}/shifts/${payload.shift.id}`,
      myInit,
    );
  },
  async assignUserToRota(context, payload) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: {
        userId: payload.userId,
        agencyId: payload.agencyId,
        displayName: payload.displayName,
      },
    };
    const api = this.$api;
    if (payload.doNotCatchIfError) {
      api.doNotCatchIfError();
    }
    return api.post(
      API_NAME,
      `${API_VERSION}/rotas/${payload.rotaId}/shifts/${payload.shiftId}/assign`,
      myInit,
    );
  },
  async reassignRotaShift(context, payload) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: omit(payload, ["rotaId", "shiftId"]),
    };
    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rotas/${payload.rotaId}/shifts/${payload.shiftId}/assign`,
      myInit,
    );
  },
  async getRotaShifts(context, payload) {
    const api = this.$api;
    if (payload.doNotCatchIfError) {
      api.doNotCatchIfError();

      delete payload.doNotCatchIfError;
    }
    const myInit = {
      queryStringParameters: {
        ...payload,
      },
    };
    return api.get(API_NAME, `${API_VERSION}/rotas/shifts`, myInit);
  },

  async getPcsClientActivities(context, payload) {
    const api = this.$api;
    if (payload.doNotCatchIfError) {
      api.doNotCatchIfError();

      delete payload.doNotCatchIfError;
    }
    const myInit = {
      queryStringParameters: {
        ...payload,
      },
    };
    return api
      .get(
        API_NAME,
        `${API_VERSION}/clients/${payload.clientId}/pcs/activities`,
        myInit,
      )
      .then((response) => response);
  },

  async getNowNext(context, payload) {
    const api = this.$api;
    if (payload.doNotCatchIfError) {
      api.doNotCatchIfError();

      delete payload.doNotCatchIfError;
    }
    const myInit = {
      queryStringParameters: {
        ...payload,
      },
    };
    return api.get(API_NAME, `${API_VERSION}/rotas/now-next`, myInit);
  },
};

export default {
  namespaced: true,
  actions,
};
